/**
 * Extract scene-level action rows from a shot's parsed panels.
 *
 * (See file body for full docstring.)
 */

// Small, dependency-free id generator — avoids ESM import issues in Jest
// (the `uuid` package ships as ESM which Jest's CommonJS runtime chokes on).
let _seq = 0;
const nextId = () => `panel-scene-${Date.now().toString(36)}-${(_seq++).toString(36)}`;
const parseTimecodeToSec = (str) => {
    if (str === null || str === undefined) return null;
    const s = String(str).trim();
    if (!s) return null;
    if (s.includes(':')) {
        const [mm, ss] = s.split(':');
        return (parseInt(mm, 10) || 0) * 60 + (parseFloat(ss) || 0);
    }
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
};

const parsePanelTimecode = (timecode) => {
    if (!timecode || typeof timecode !== 'string') return null;
    const parts = timecode.split(/\s*[–-]\s*/);
    if (parts.length < 2) return null;
    const startSec = parseTimecodeToSec(parts[0]);
    const endSec   = parseTimecodeToSec(parts[1]);
    if (startSec === null || endSec === null) return null;
    return { startSec, endSec };
};

const isPurelyDialogueLine = (text) => {
    if (!text) return false;
    // "Focus tight on Tara as they say 'Hey, can you...'" — pure dialogue.
    return /\bas\s+(?:they|he|she)\s+say(?:s)?\s+"/i.test(text)
        || /^\s*"[^"]+"\s*$/i.test(text);
};

export const extractSceneActionsFromPanels = (panels) => {
    if (!Array.isArray(panels) || panels.length === 0) return [];
    const rows = [];
    for (const panel of panels) {
        const bounds = parsePanelTimecode(panel?.timecode || '');
        if (!bounds) continue;

        // 1) Camera movement row — skip if empty / "static" / "none".
        const cam = String(panel?.cameraMovement || '').trim();
        if (cam && !/^(?:none|n\/a|static\s*(?:framing|shot|hold)?|steady framing)$/i.test(cam)) {
            rows.push({
                id: nextId(),
                startTime: `${bounds.startSec.toFixed(1)}s`,
                endTime:   `${bounds.endSec.toFixed(1)}s`,
                action: `📷 ${cam}`,
                source: 'panel',
                subjectType: 'camera',
                rawAction: cam,
            });
        }

        // 2) Visual composition row — skip if the text is purely a dialogue
        //    quote (already covered by character_actions).
        const viz = String(panel?.visualComposition || '').trim();
        if (viz && !isPurelyDialogueLine(viz)) {
            rows.push({
                id: nextId(),
                startTime: `${bounds.startSec.toFixed(1)}s`,
                endTime:   `${bounds.endSec.toFixed(1)}s`,
                action: `🎬 ${viz}`,
                source: 'panel',
                subjectType: 'staging',
                rawAction: viz,
            });
        }
    }
    return rows;
};

export default extractSceneActionsFromPanels;
